package net.sergeych.ugdx.shared.trader

import com.ionspin.kotlin.bignum.decimal.BigDecimal
import io.ktor.utils.io.CancellationException
import kotlinx.coroutines.*
import kotlinx.coroutines.flow.*
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlinx.datetime.Clock
import kotlinx.datetime.Instant
import net.sergeych.mp_logger.LogTag
import net.sergeych.mp_logger.exception
import net.sergeych.mp_logger.info
import net.sergeych.mp_tools.CachedRefreshingValue
import net.sergeych.ugdx.GuestAPI
import net.sergeych.ugdx.realtime.RealtimeChannel
import net.sergeych.ugdx.shared.api.*
import kotlin.random.Random
import kotlin.time.ExperimentalTime

object Trader : LogTag("XCHG") {
    /**
     * Get static historic data
     */
    @Suppress("UNUSED_PARAMETER")
    fun getHistory(
        currencyPair: CurrencyPair,
        start: Instant,
        end: Instant?,
        interval: HistoryInterval,
    ): CandleStickSeries {
        TODO()
    }

    private val orderbookMutex = Mutex()
    private val orderbooks = mutableMapOf<String, StateFlow<OrderbookSnapshot?>>()

    /**
     * Get the StateFlow for the ordebook of a given pair. The result, [OrderbookSnapshot], is null if no
     * orderbook currently exists or data could not be obtained. Use its `value` to get most recencly known
     * state. Note that caching its results is extremely bad idea. `collect` it:
     *
     * ```
     * Trader.orderbookFor(cp).collect {
     *      it?.orders?.let { xx ->
     *          println("----- orders ---------")
     *          for (o in xx) println("   $o")
     *          println("----------------------")
     *      } ?: println("no orders")
     *}
     *```
     *
     * Also please note that its `value` could be null if no valid orderbook snapshot was ever loaded, and it won't
     * refresh it until _you collect the flow!_.
     */
    suspend fun orderbookFor(currencyPair: CurrencyPair): StateFlow<OrderbookSnapshot?> =
        orderbookMutex.withLock {
            orderbooks.getOrPut(currencyPair.toString()) {
                createSnapshotsFlow(currencyPair)
            }
        }

    /**
     * Get depth of market data as flow. The flow returns last known DOM state and then receive
     * pushes from the service in almsot realtime manner (rate limits m,ay apply though).
     */
    suspend fun depthOfMarketFlow(currencyPair: CurrencyPair): Flow<net.sergeych.ugdx.shared.trader.DepthOfMarket> =
        channelFlow {
            info { "watching par $currencyPair" }
            RealtimeChannel.watchPair(currencyPair, true)
            // The problem is, server returns last DOM but only the first time, so supposedly we
            // ,ay already askt for it, we re-ask it:
            var last: net.sergeych.ugdx.shared.trader.DepthOfMarket? = cmdTraderGetDepthOfMarket(currencyPair).also { send(it) }
            coroutineScope {
                launch {
                    // if reconnect:
                    GuestAPI().loggedInFlow.collect {
                        if( it ) send( cmdTraderGetDepthOfMarket(currencyPair) )
                    }
                }
                GuestAPI().pushes.collect {
                    info { "collected: $it" }
                    if (it is PushPayload.DepthOfMarketChanged) {
                        if (last != null && it.depthOfMarket == last)
                            last = null // we just returned it!
                        else
                            send(it.depthOfMarket)
                    }
                }
            }
        }


    /**
     * Get the flow of trade order change its state (e.g. when exchange engine matches it, etc). Note that there
     * could be race conditions, so we recommend to collect changes first, them request list of orders to fill
     * visible elements, and then update it by collecting changes.
     *
     * User must be logged in for this method to perform.
     */
    suspend fun orderChangeFlow(): Flow<TradeOrder> =
        flow {
            RealtimeChannel.watchUser(true)
            GuestAPI().pushes.collect {
                println("push collected: $it")
                if (it is PushPayload.TraderOrderChanged)
                    emit(it.order)
            }
        }

    private fun createSnapshotsFlow(currencyPair: CurrencyPair): MutableStateFlow<OrderbookSnapshot?> {
        info { "creor-1 $currencyPair" }
        var job: Job? = null
        var lastInstant: Instant? = null
        return MutableStateFlow<OrderbookSnapshot?>(null)
            .also { snapshotFlow ->
                info { "sf: $snapshotFlow" }
                GlobalScope.launch {
                    snapshotFlow.subscriptionCount.collect {
                        if (it > 0 && job == null) {
                            info { "starting orderbook watcher $currencyPair" }
                            job = GlobalScope.launch {
                                try {
                                    while (isActive) {
                                        try {
                                            val snapshot: OrderbookSnapshot =
                                                cmdTraderGetBook(TraderGetBookArgs(currencyPair, lastInstant))
                                            if (lastInstant != snapshot.createdAt) {
                                                lastInstant = snapshot.createdAt
                                                snapshotFlow.value = snapshot
                                            }
                                        } catch (t: Throwable) {
                                            exception { "failed to get orderbook for $currencyPair" to t }
                                        }
                                        delay(1000)
                                    }
                                } catch (x: CancellationException) {
                                    // its ok
                                }
                            }
                        } else if (it < 1 && job != null) {
                            info { "cancelling orderbook watcher $currencyPair" }
                            job?.cancel(null)
                            job = null
                        }
                    }
                }
            }
    }

    private val cachedCurrencyPairs = CachedRefreshingValue<List<CurrencyPair>>(50000) {
        cmdTraderListCurrencyPairs().currencyPairs
    }

    /**
     * Get list of currency pairs that are right now processed by the service. It is reasonably cached so
     * could be called repeatedly.
     */
    suspend fun currencyPairs(): List<CurrencyPair> = cachedCurrencyPairs.get()

    suspend fun currencyPair(label: String): CurrencyPair? = cachedCurrencyPairs.get().find { it.label == label }




    /**
     * Get a shared flow that emits realtime updates of candlesticks data. Get the flow and collect it. The flow
     * may provide some playback data (e.g. most recent events) so pay attention to [CandleStickEvent] fields.
     */
    @Deprecated("uses Stats().flow() instead")
    fun getFlow(
        currencyPair: CurrencyPair,
        interval: HistoryInterval,
    ): SharedFlow<CandleStickEvent> = getTestFlow(currencyPair, interval)

    fun getTestFlow(
        currencyPair: CurrencyPair,
        interval: HistoryInterval,
    ): SharedFlow<CandleStickEvent> {
        val flow = MutableSharedFlow<CandleStickEvent>(1024, 1024)
        GlobalScope.launch {
            var stick = CandleStick.randomAround(Random.nextDouble(17.0, 211.2))
            var start = Clock.System.now()
            while (isActive) {
                flow.emit(CandleStickEvent(currencyPair, start, interval, stick))
                delay(interval.duration)
                start += interval.duration
                stick = CandleStick.randomAround(stick.median ?: 0.0)
            }
        }
        return flow.asSharedFlow()
    }

    /**
     * Create new trade order. If returns order, it means it is already created in the exchange service.
     * The most oftend error code is [ErrorCode.INVALID_ORDER] though. some important notes:
     *
     * - match [isBuy] direction and buy/sell accounts! If you buy the pair, then you sell quote currency and
     *   buy base currency, and vice versa. The account should be of proper currency!
     *
     * - set [expiresAt] to a some not too close instant or it risk of not being processed at all.
     *
     * - be sure that selling account have enough funds to perform the reqiested operations.
     *
     * @return the created order.
     * @throws ApiException, most likely [ErrorCode.INVALID_ORDER]
     */
    suspend fun createOrder(
        currencyPair: CurrencyPair,
        isBuy: Boolean,
        amount: BigDecimal,
        limit: BigDecimal?,
        stop: String?,
        allOrNothing: Boolean,
        buyAccount: ApiAccount,
        sellAccount: ApiAccount,
        expiresAt: Instant,
    ): TradeOrder =
        cmdTraderCreateOrder(TradeOrderCreationArgs(
            currencyPair, isBuy, amount, limit, stop, allOrNothing, buyAccount.id, sellAccount.id, expiresAt
        ))

    /**
     * Get the current state of the order
     */
    suspend fun refreshOrder(tradeOrder: TradeOrder): TradeOrder = cmdTraderGetOrder(TraderOrderIdArgs(tradeOrder.id))

    /**
     * Update order to match new state. __Important: old order will be REMOVED and new one will be added__, so new
     * [id] _will be different_. If the order is already executed/removed/rejected its state can't be changed anymore,
     * just create new order.
     *
     * Also take note that it is possible that order to update will be executed while the command is being transmitted
     * over the net, so it might be better to remove it manually with [removeOrder].
     *
     * @return new order with requested parameters
     */
    suspend fun updateOrder(tradeOrder: TradeOrder): TradeOrder = cmdTraderUpdateOrder(tradeOrder)

    /**
     * Remove order of it is yet active, e.g. its `status` returns true with [TradeOrder.Status.isSuitableForPorcessing]
     * otherwise it can't be removed.
     *
     * Note that partially executed order also can be removed, but it will not change deals already performed with it.
     */
    suspend fun removeOrder(tradeOrder: TradeOrder): TradeOrder = cmdTraderDeleteOrder(TraderOrderIdArgs(tradeOrder.id))

    /**
     * Get list of most recent (own) trade orders, sorted most recent first. See [TradeOrders] for more details.
     */
    suspend fun getOrders(
        currencyPair: CurrencyPair,
        beforeId: Long? = null,
        onlyActive: Boolean = false,
        limit: Int = 50,
    ): TradeOrders =
        cmdTraderMyOrders(TradeListOrderArgs(currencyPair, beforeId, limit, onlyActive))


    suspend fun getMarketDepth(currencyPair: CurrencyPair): net.sergeych.ugdx.shared.trader.DepthOfMarket =
        cmdTraderGetDepthOfMarket(currencyPair)

}

